import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import { Hvc } from '../components';
import type { SearchModel } from './search-helpers';

interface IInputSearchOptions {
	showOptions: boolean;
	searchTerm: string;
	model: SearchModel;
	highlightedIndex: number;
	setHighlightedIndex: (value: number) => void;
	onSelect: (item: { label: string; value: string | number }) => void;
	compact?: boolean;
}

const LIST_HEIGHT = 280;
const OVERSCAN = 6;

export const InputSearchOptions: FC<IInputSearchOptions> = ({
	showOptions,
	searchTerm,
	model,
	highlightedIndex,
	setHighlightedIndex,
	onSelect,
	compact = false,
}) => {
	const listRef = useRef<HTMLDivElement>(null);
	const [scrollTop, setScrollTop] = useState(0);
	const totalHeight = useMemo(() => model.entries.reduce((sum, entry) => sum + entry.height, 0), [model.entries]);
	const maxListHeight = compact ? 220 : LIST_HEIGHT;
	const listHeight = Math.min(totalHeight || maxListHeight, maxListHeight);

	const offsets = useMemo(() => {
		let acc = 0;
		return model.entries.map((entry) => {
			const current = acc;
			acc += entry.height;
			return current;
		});
	}, [model.entries]);

	const { startIndex, endIndex } = useMemo(() => {
		if (!model.entries.length) return { startIndex: 0, endIndex: 0 };
		const startMatch = offsets.findIndex((offset, index) => offset + model.entries[index].height >= scrollTop);
		const start = Math.max((startMatch === -1 ? 0 : startMatch) - OVERSCAN, 0);
		const endMatch = offsets.findIndex((offset) => offset >= scrollTop + listHeight);
		const endBase = endMatch === -1 ? model.entries.length - 1 : endMatch;
		const end = Math.min(model.entries.length - 1, endBase + OVERSCAN);
		return { startIndex: start, endIndex: end };
	}, [offsets, scrollTop, model.entries, listHeight]);

	const visibleEntries = model.entries.slice(startIndex, endIndex + 1);
	const topSpacer = offsets[startIndex] ?? 0;
	const bottomSpacer = Math.max(totalHeight - (offsets[endIndex] ?? 0) - (model.entries[endIndex]?.height ?? 0), 0);

	const highlightedEntryIndex = model.selectableEntries[highlightedIndex]?.entryIndex ?? 0;

	useEffect(() => {
		if (!listRef.current) return;
		const entryOffset = offsets[highlightedEntryIndex] ?? 0;
		if (entryOffset < scrollTop) {
			listRef.current.scrollTop = entryOffset;
		} else if (entryOffset > scrollTop + listHeight - 46) {
			listRef.current.scrollTop = Math.max(entryOffset - listHeight + 46, 0);
		}
	}, [highlightedEntryIndex, offsets, scrollTop, listHeight]);

	const handleAlphabetJump = (letter: string) => {
		const targetOffset = model.groupOffsets[letter];
		if (typeof targetOffset !== 'number' || !listRef.current) return;
		listRef.current.scrollTop = targetOffset;
	};

	return (
		<Hvc
			removeDOM
			view={showOptions}
		>
			<div
				className={`search-container rounded ${compact ? 'compact' : ''}`}
				onMouseDown={(e) => e.stopPropagation()}
				role='listbox'
			>
				{!compact && (
					<div className='search-header'>
						<span className='search-count'>{searchTerm ? `${model.resultCount} results` : `${model.resultCount} available`}</span>
						<span className='search-hint'>
							{searchTerm ? `Searching for "${searchTerm}"` : 'Type to search by name or acronym'}
						</span>
					</div>
				)}
				<div
					className='search-list'
					ref={listRef}
					onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
					style={{ maxHeight: listHeight, height: listHeight }}
				>
					<div style={{ height: topSpacer }} />
					{visibleEntries.map((entry, index) => {
						if (entry.type === 'section') {
							return (
								<div
									key={entry.key}
									className='search-section'
								>
									{entry.label}
								</div>
							);
						}
						if (entry.type === 'group') {
							return (
								<div
									key={entry.key}
									className='search-group'
								>
									{entry.label}
								</div>
							);
						}
						if (entry.type === 'empty') {
							return (
								<div
									key={entry.key}
									className='search-empty'
								>
									{entry.label}
								</div>
							);
						}
						if (entry.type === 'item' && entry.item) {
							const isHighlighted = highlightedEntryIndex === startIndex + index;
							const hasCode = Boolean(entry.display?.code);
							return (
								<div
									key={entry.key}
									className={`search-item ${isHighlighted ? 'is-active' : ''} ${hasCode ? '' : 'no-code'}`}
									onMouseEnter={() => {
										const listIndex = model.selectableEntries.findIndex((item) => item.entryIndex === startIndex + index);
										if (listIndex >= 0) setHighlightedIndex(listIndex);
									}}
									onMouseDown={() => onSelect(entry.item!)}
								>
									{hasCode ? <div className='search-item-code'>{entry.display?.primary}</div> : null}
									<div className='search-item-meta'>
										<span className='search-item-name'>{entry.display?.secondary ?? entry.display?.primary}</span>
									</div>
								</div>
							);
						}
						return null;
					})}
					<div style={{ height: bottomSpacer }} />
				</div>
				{model.groupKeys.length > 0 && !compact && (
					<div className='search-alpha'>
						{model.groupKeys.map((letter) => (
							<button
								type='button'
								key={letter}
								onMouseDown={() => handleAlphabetJump(letter)}
							>
								{letter}
							</button>
						))}
					</div>
				)}
			</div>
		</Hvc>
	);
};
